import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuietMode } from '../components/QuietModeProvider'
import { useAuth } from '../components/AuthProvider'
import { useTheme } from '../components/ThemeProvider'
import QuietModeToggle from '../components/QuietModeToggle'
import ThemeToggle from '../components/ThemeToggle'
import AuthModal from '../components/AuthModal'
import NaturalBackground from '../components/NaturalBackground'
import MindPolishLogo from '../components/MindPolishLogo'

const Home = () => {
  const navigate = useNavigate()
  const { isQuietMode, isLowStimMode } = useQuietMode()
  const { user, signOut } = useAuth()
  const { theme } = useTheme()
  const [showSettings, setShowSettings] = useState(false)
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [signOutLoading, setSignOutLoading] = useState(false)
  const [signOutError, setSignOutError] = useState<string | null>(null)

  const handleSignOut = async () => {
    try {
      setSignOutLoading(true)
      setSignOutError(null)
      await signOut()
      console.log("Sign out successful from Home component")
    } catch (error) {
      console.error('Error signing out:', error)
      setSignOutError('Failed to sign out. Please try again.')
    } finally {
      setSignOutLoading(false)
    }
  }

  return (
    <NaturalBackground variant="sky">
      <div className="min-h-screen flex flex-col">
        <div className="flex-1 flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 py-4 sm:py-16">
          {/* Top Header - Mind Polish logo left, controls right */}
          <div className="absolute top-4 sm:top-8 left-4 sm:left-8 right-4 sm:right-8">
            <div className="flex items-center justify-between">
              {/* Mind Polish Logo - Left side */}
              <MindPolishLogo size="sm" />
              
              {/* Controls Group - Right side, all on same level */}
              <div className="flex items-center space-x-2 sm:space-x-3">
                <ThemeToggle size="sm" />
                <button
                  onClick={() => setShowSettings(!showSettings)}
                  className="p-2 sm:p-3 rounded-xl sm:rounded-2xl bg-sage-50/60 hover:bg-sage-100/60 
                             dark:bg-sage-800/60 dark:hover:bg-sage-700/60
                             border border-sage-100/40 dark:border-sage-700/40 hover:border-sage-200/50 dark:hover:border-sage-600/50
                             transition-colors duration-500 focus:outline-none focus:ring-4 focus:ring-sage-100/50 dark:focus:ring-sage-600/50"
                  title="Settings"
                >
                  <span className="text-sm sm:text-base">⚙️</span>
                </button>
                <a 
                  href="https://bolt.new" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block p-2 sm:p-3 rounded-xl sm:rounded-2xl bg-sage-50/60 hover:bg-sage-100/60 
                             dark:bg-sage-800/60 dark:hover:bg-sage-700/60
                             border border-sage-100/40 dark:border-sage-700/40 hover:border-sage-200/50 dark:hover:border-sage-600/50
                             transition-all duration-500 focus:outline-none focus:ring-4 focus:ring-sage-100/50 dark:focus:ring-sage-600/50"
                  title="Powered by Bolt"
                >
                  <img 
                    src="/white_circle_360x360.png" 
                    alt="Powered by Bolt" 
                    className="w-4 h-4 sm:w-5 sm:h-5"
                  />
                </a>
              </div>
            </div>
          </div>

          {/* Auth Status - Positioned below header */}
          <div className="absolute top-16 sm:top-20 left-4 sm:left-8 right-4 sm:right-8">
            <div className="flex items-center justify-center sm:justify-start">
              {user ? (
                <div className="flex items-center space-x-2 text-center sm:text-left">
                  <div className="w-1.5 h-1.5 bg-mint-400 dark:bg-mint-300 rounded-full"></div>
                  <span className="text-sage-500 dark:text-sage-300 text-xs font-light">Signed in</span>
                  <button
                    onClick={handleSignOut}
                    disabled={signOutLoading}
                    className="text-sage-400 dark:text-sage-400 text-xs font-light hover:text-sage-500 dark:hover:text-sage-300 transition-colors disabled:opacity-50"
                  >
                    {signOutLoading ? 'Signing out...' : 'Sign out'}
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => navigate('/login')}
                  className="flex items-center space-x-1.5 px-3 py-1.5 bg-sage-50/40 dark:bg-sage-800/40 hover:bg-sage-100/50 dark:hover:bg-sage-700/50
                             text-sage-600 dark:text-sage-300 rounded-xl text-xs font-light border border-sage-100/30 dark:border-sage-700/30 hover:border-sage-200/40 dark:hover:border-sage-600/40
                             transition-colors duration-300 focus:outline-none focus:ring-4 focus:ring-sage-100/50 dark:focus:ring-sage-600/30"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span>Sign in</span>
                </button>
              )}
            </div>
          </div>

          {/* Settings Panel - Positioned relative to settings button */}
          {showSettings && (
            <div className="absolute top-16 sm:top-20 left-4 sm:left-auto right-4 sm:right-8 z-20">
              <div className="bg-white/80 dark:bg-sage-800/80 backdrop-blur-sm rounded-3xl border border-sage-100/50 dark:border-sage-700/50 p-4 sm:p-6 shadow-lg">
                <QuietModeToggle />
              </div>
            </div>
          )}

          {/* Sign Out Error Message */}
          {signOutError && (
            <div className="absolute top-28 sm:top-32 left-4 sm:left-8 right-4 sm:right-8">
              <div className="bg-peach-50/60 dark:bg-peach-900/30 p-3 rounded-xl border border-peach-100/40 dark:border-peach-700/30 text-center max-w-xs mx-auto">
                <p className="text-peach-600 dark:text-peach-300 text-xs font-light">{signOutError}</p>
              </div>
            </div>
          )}

          {/* Welcome Section - Better mobile spacing */}
          <div className={`text-center ${isLowStimMode ? 'mb-16 sm:mb-32' : 'mb-12 sm:mb-20'} mt-20 sm:mt-0`}>
            <h1 className={`text-sage-600 dark:text-sage-200 font-light leading-relaxed max-w-sm mx-auto mb-3 sm:mb-4 ${isLowStimMode ? 'text-xl sm:text-2xl' : 'text-base sm:text-lg'}`}>
              Welcome to your quiet space
            </h1>
            <p className={`text-sage-500 dark:text-sage-300 font-light leading-relaxed max-w-sm mx-auto ${isLowStimMode ? 'text-base sm:text-lg' : 'text-sm sm:text-base'}`}>
              Take a breath. This moment is yours.
            </p>
          </div>

          {/* Main Navigation - Improved mobile spacing */}
          <div className={`w-full max-w-sm ${isLowStimMode ? 'space-y-8 sm:space-y-12' : 'space-y-5 sm:space-y-6'}`}>
            {/* I'm OK Button */}
            <button
              onClick={() => navigate('/tools')}
              className={`w-full bg-sage-50/60 hover:bg-sage-100/60 
                         dark:bg-sage-800/60 dark:hover:bg-sage-700/60
                         text-sage-700 dark:text-sage-200 font-light rounded-3xl 
                         border border-sage-100/40 dark:border-sage-700/40 hover:border-sage-200/50 dark:hover:border-sage-600/50
                         transition-all duration-700 ease-out
                         focus:outline-none focus:ring-4 focus:ring-sage-100/50 dark:focus:ring-sage-600/30 focus:ring-offset-2 focus:ring-offset-transparent
                         shadow-sm hover:shadow-md ${isLowStimMode ? 'py-10 sm:py-12 px-8 sm:px-10' : 'py-8 sm:py-10 px-6 sm:px-8'}`}
            >
              <div className="flex flex-col items-center space-y-3 sm:space-y-4">
                <div className={`${isLowStimMode ? 'text-2xl sm:text-3xl' : 'text-xl sm:text-2xl'}`}>🌱</div>
                <span className={`font-light ${isLowStimMode ? 'text-xl sm:text-2xl' : 'text-lg sm:text-xl'}`}>
                  I am ok
                </span>
                {!isQuietMode && (
                  <p className={`text-sage-500 dark:text-sage-300 font-light leading-relaxed text-center ${isLowStimMode ? 'text-base sm:text-lg' : 'text-xs sm:text-sm'}`}>
                    Explore tools for maintaining your wellbeing
                  </p>
                )}
              </div>
            </button>

            {/* I Need Help Button */}
            <button
              onClick={() => navigate('/support')}
              className={`w-full bg-peach-50/60 hover:bg-peach-100/60 
                         dark:bg-peach-900/30 dark:hover:bg-peach-800/40
                         text-peach-700 dark:text-peach-300 font-light rounded-3xl 
                         border border-peach-100/40 dark:border-peach-700/30 hover:border-peach-200/50 dark:hover:border-peach-600/40
                         transition-all duration-700 ease-out
                         focus:outline-none focus:ring-4 focus:ring-peach-100/50 dark:focus:ring-peach-700/30 focus:ring-offset-2 focus:ring-offset-transparent
                         shadow-sm hover:shadow-md ${isLowStimMode ? 'py-10 sm:py-12 px-8 sm:px-10' : 'py-8 sm:py-10 px-6 sm:px-8'}`}
            >
              <div className="flex flex-col items-center space-y-3 sm:space-y-4">
                <div className={`${isLowStimMode ? 'text-2xl sm:text-3xl' : 'text-xl sm:text-2xl'}`}>⚡</div>
                <span className={`font-light ${isLowStimMode ? 'text-xl sm:text-2xl' : 'text-lg sm:text-xl'}`}>
                  I need help
                </span>
                {!isQuietMode && (
                  <p className={`text-peach-500 dark:text-peach-300 font-light leading-relaxed text-center ${isLowStimMode ? 'text-base sm:text-lg' : 'text-xs sm:text-sm'}`}>
                    Support for when you're feeling overwhelmed or burned out
                  </p>
                )}
              </div>
            </button>
          </div>

          {/* Gentle Encouragement - Better mobile spacing */}
          {!isQuietMode && (
            <div className={`text-center ${isLowStimMode ? 'mt-12 sm:mt-20' : 'mt-10 sm:mt-16'}`}>
              <p className={`text-sage-400 dark:text-sage-400 font-light leading-relaxed max-w-xs mx-auto ${isLowStimMode ? 'text-sm sm:text-base' : 'text-xs sm:text-sm'}`}>
                You're doing beautifully by being here
              </p>
              {user && (
                <p className={`text-sage-400 dark:text-sage-500 font-light leading-relaxed max-w-xs mx-auto mt-1 sm:mt-2 ${isLowStimMode ? 'text-xs sm:text-sm' : 'text-xs'}`}>
                  Your progress is being saved securely
                </p>
              )}
            </div>
          )}

          {/* Natural Elements Footer - Hidden on small mobile screens */}
          {!isLowStimMode && (
            <div className="mt-12 sm:mt-20 hidden sm:flex space-x-8 opacity-60">
              <div className="w-8 h-8 bg-sage-100/40 dark:bg-sage-700/40 rounded-full flex items-center justify-center">
                <div className="w-3 h-3 bg-sage-200/60 dark:bg-sage-600/60 rounded-full"></div>
              </div>
              <div className="w-8 h-8 bg-mint-100/40 dark:bg-mint-900/40 rounded-full flex items-center justify-center">
                <div className="w-3 h-3 bg-mint-200/60 dark:bg-mint-700/60 rounded-full"></div>
              </div>
              <div className="w-8 h-8 bg-cream-100/40 dark:bg-cream-900/40 rounded-full flex items-center justify-center">
                <div className="w-3 h-3 bg-cream-200/60 dark:bg-cream-700/60 rounded-full"></div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Navigation - Improved mobile layout */}
        <footer className="bg-white/40 dark:bg-sage-800/40 backdrop-blur-sm border-t border-sage-100/30 dark:border-sage-700/30 p-3 sm:p-6 mt-auto">
          <div className="max-w-lg mx-auto">
            <nav className="grid grid-cols-5 gap-1 sm:gap-2">
              {[
                { path: '/home', label: 'Home', icon: '🏠' },
                { path: '/about', label: 'About', icon: '📄' },
                { path: '/contact', label: 'Contact', icon: '📬' },
                { path: '/privacy', label: 'Privacy', icon: '🔐' },
                { path: '/terms', label: 'Terms', icon: '📜' }
              ].map((link) => (
                <button
                  key={link.path}
                  onClick={() => navigate(link.path)}
                  className={`flex flex-col items-center space-y-1 p-1.5 sm:p-2 rounded-lg sm:rounded-xl transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-sage-100 dark:focus:ring-sage-600 ${
                    location.pathname === link.path
                      ? 'text-sage-600 dark:text-sage-300'
                      : 'text-sage-400 dark:text-sage-500 hover:text-sage-600 dark:hover:text-sage-300'
                  }`}
                >
                  <span className="text-xs sm:text-base">{link.icon}</span>
                  <span className="text-xs font-light">{link.label}</span>
                </button>
              ))}
            </nav>
            
            <div className="text-center mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-sage-100/30 dark:border-sage-700/30">
              <p className="text-sage-400 dark:text-sage-500 text-xs font-light">
                Mind Polish • Built with care for your wellbeing
              </p>
            </div>
          </div>
        </footer>

        {/* Auth Modal */}
        <AuthModal 
          isOpen={showAuthModal} 
          onClose={() => setShowAuthModal(false)} 
        />
      </div>
    </NaturalBackground>
  )
}

export default Home